import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import DownloadIcon from '@/assets/download.svg?react';
import { useNotifications } from '@/hooks/useNotifications';
import { fetchGamesExport } from '@/lib/api';
import { saveBlobAsFile } from '@/lib/download';

// The grid only treats a search as active from two characters, so the export
// must use the same threshold or the two would disagree.
const MIN_SEARCH_LENGTH = 2;

type ExportToExcelButtonProps = {
  filterParams?: Record<string, unknown>;
  q?: string;
  className?: string;
};

const ExportToExcelButton = ({
  filterParams,
  q = '',
  className,
}: ExportToExcelButtonProps) => {
  const intl = useIntl();
  const { showError } = useNotifications();

  const label = intl.formatMessage({
    id: 'exportToExcel',
    defaultMessage: 'Export to Excel',
  });

  const { mutate: exportToExcel, isPending } = useMutation<
    Blob,
    AxiosError,
    void
  >({
    // A mutation, not a query: query results are cached for an hour
    // globally, which would hand back a stale workbook on a second click.
    mutationFn: () => {
      const trimmed = q.trim();
      return fetchGamesExport({
        ...filterParams,
        ...(trimmed.length >= MIN_SEARCH_LENGTH
          ? { q: trimmed }
          : {}),
      });
    },
    onSuccess: (blob) => {
      // Content-Disposition is not readable: the backend sets no
      // expose_headers, so the name is built here.
      saveBlobAsFile(
        blob,
        `game-portfolio-${dayjs().format('YYYY-MM-DD')}.xlsx`,
      );
    },
    onError: (error) => {
      showError(
        error.response?.status === 403
          ? 'User lacks required permission'
          : 'Failed to export games',
      );
    },
  });

  return (
    <StyledButton
      className={className}
      variant="text"
      onClick={() => exportToExcel()}
      disabled={isPending}
      endIcon={
        isPending ? (
          <CircularProgress size={16} />
        ) : (
          <DownloadIcon />
        )
      }
      aria-label={label}
    >
      <FormattedMessage
        id="exportToExcel"
        defaultMessage="Export to Excel"
      />
    </StyledButton>
  );
};

const StyledButton = styled(Button)(() => ({
  textWrap: 'nowrap',

  '.MuiButton-endIcon': {
    marginLeft: 4,

    svg: {
      width: 16,
      height: 16,
    },

    // download.svg hardcodes stroke="white" because its other callers sit on
    // dark surfaces. This button is on a light one, so the icon takes the
    // button's own colour instead of vanishing.
    'svg path': {
      stroke: 'currentColor',
    },
  },
})) as typeof Button;

export default ExportToExcelButton;
